import { useApolloClient, useQuery } from "@apollo/client";
import Close from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { WasteInvoiceImportRunQuery, WasteInvoiceImportStatus } from "generated-graphql/graphql";
import { useEffect, useRef } from "react";

import { WASTE_INVOICE_IMPORT_RUN_QUERY, WASTE_INVOICES_QUERY } from "../queries";
import { IMPORT_VENDOR_LABELS } from "../utils";
import { InvoiceImportStatusChip } from "./InvoiceImportStatusChip";

const ACTIVE_STATUSES = new Set<WasteInvoiceImportStatus>([
  WasteInvoiceImportStatus.Queued,
  WasteInvoiceImportStatus.Running,
  WasteInvoiceImportStatus.IngestionQueued,
  WasteInvoiceImportStatus.Ingesting,
]);

type ImportRun = WasteInvoiceImportRunQuery["wasteInvoiceImportRun"];

function RunMessage({ run }: { run: ImportRun }) {
  if (ACTIVE_STATUSES.has(run.status)) {
    let message = "The workbook is being validated.";

    if (run.status === WasteInvoiceImportStatus.IngestionQueued) {
      message = "The validated workbook is queued for import.";
    } else if (run.status === WasteInvoiceImportStatus.Ingesting) {
      message = "The validated workbook is being imported.";
    }

    return (
      <Alert severity="info" icon={<CircularProgress size={18} />}>
        {message}
      </Alert>
    );
  }
  if (run.status === WasteInvoiceImportStatus.Succeeded) {
    return <Alert severity="success">Invoice import completed successfully.</Alert>;
  }
  if (run.status === WasteInvoiceImportStatus.Cancelled) {
    return <Alert severity="warning">The workbook import was cancelled.</Alert>;
  }

  return <Alert severity="error">Invoice import failed.</Alert>;
}

export function InvoiceImportStatus({
  runId,
  canImport,
  onClose,
  onChooseAnother,
}: {
  runId: string;
  canImport: boolean;
  onClose: () => void;
  onChooseAnother: () => void;
}) {
  const client = useApolloClient();
  const previousStatus = useRef<WasteInvoiceImportStatus | null>(null);
  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery(
    WASTE_INVOICE_IMPORT_RUN_QUERY,
    { variables: { id: runId }, fetchPolicy: "network-only" }
  );
  const run = data?.wasteInvoiceImportRun;

  useEffect(() => {
    if (run != null && ACTIVE_STATUSES.has(run.status)) {
      startPolling(3000);
      return stopPolling;
    }
    stopPolling();
    return undefined;
  }, [run, startPolling, stopPolling]);

  useEffect(() => {
    if (
      run?.status === WasteInvoiceImportStatus.Succeeded &&
      previousStatus.current !== WasteInvoiceImportStatus.Succeeded
    ) {
      void client.refetchQueries({ include: [WASTE_INVOICES_QUERY] });
    }
    previousStatus.current = run?.status ?? null;
  }, [client, run?.status]);

  if (loading && run == null) {
    return (
      <Card sx={{ mx: 3, mt: 3 }}>
        <CardContent>
          <CircularProgress size={18} />
        </CardContent>
      </Card>
    );
  }
  if (error != null || run == null) {
    return (
      <Alert
        severity="warning"
        sx={{ mx: 3, mt: 3 }}
        action={<Button onClick={() => void refetch()}>Check again</Button>}
      >
        The import status could not be loaded. Run {runId}.
      </Alert>
    );
  }

  return (
    <Card sx={{ mx: 3, mt: 3 }}>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1" fontWeight={600}>
                  {run.sourceDocument.title}
                </Typography>
                <InvoiceImportStatusChip status={run.status} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {IMPORT_VENDOR_LABELS[run.vendor]} invoice import · Run {run.id}
              </Typography>
            </Box>
            <IconButton aria-label="Dismiss import status" size="small" onClick={onClose}>
              <Close fontSize="small" />
            </IconButton>
          </Stack>
          <RunMessage run={run} />
          {canImport &&
            (run.status === WasteInvoiceImportStatus.Failed ||
              run.status === WasteInvoiceImportStatus.Cancelled) && (
              <Button sx={{ alignSelf: "flex-start" }} onClick={onChooseAnother}>
                Choose another workbook
              </Button>
            )}
        </Stack>
      </CardContent>
    </Card>
  );
}
